import type { ExpertReportFeedbackItem } from '@/entities/expert-report';
import { richTextToReadableText } from '@/shared/lib';

/**
 * Комментарий правки как читаемый текст: разметка снимается, а границы абзацев остаются
 * переносами — иначе соседние абзацы слипаются в одно слово.
 */
export const formatFeedbackValue = (value: string | null): string =>
  value === null ? '' : richTextToReadableText(value);

/**
 * Оценка правки как текст. Результат блока приходит либо баллом, либо значением справочника — из
 * пары заполнено одно; значение — простой текст, разметки в нём нет.
 */
export const formatFeedbackRating = (score: number | null, valueText: string | null): string =>
  valueText ?? (score === null ? '' : String(score));

/** Оценка после правки не выставлена ни баллом, ни значением — правка вернула результат системы */
export const isRatingBackToSystem = (item: ExpertReportFeedbackItem): boolean =>
  item.ratingValue === null && item.ratingValueText === null;

/**
 * Содержимое столбца значения: правка либо показывается сравнением с предыдущим значением,
 * либо, если она этого значения не касалась, — действующим на тот момент значением без выделения.
 */
export type FeedbackCellValue = { kind: 'diff'; previous: string; next: string } | { kind: 'plain'; text: string };

/**
 * Столбец «Оценка»: изменённая оценка сравнивается с предыдущей, неизменённая показывается
 * действующей на момент правки. Оценка, не выставленная правкой ни баллом, ни значением, — возврат
 * к результату системы, поэтому сравнивается именно он.
 */
export const getRatingCell = (item: ExpertReportFeedbackItem): FeedbackCellValue => {
  const current = formatFeedbackRating(item.ratingValue, item.ratingValueText);
  if (!item.ratingChanged) return { kind: 'plain', text: current };
  return {
    kind: 'diff',
    previous: formatFeedbackRating(item.ratingPreviousValue, item.ratingPreviousValueText),
    next: isRatingBackToSystem(item)
      ? formatFeedbackRating(item.ratingSystemValue, item.ratingSystemValueText)
      : current,
  };
};

/**
 * Столбец «Комментарий»: изменённый комментарий сравнивается с предыдущим, неизменённый показывается
 * действующим на момент правки. Пустой комментарий после правки — возврат к составленному системой.
 * Замечание комментарий не правит, а дополняет, поэтому сравнивается с пустым: его текст добавлен целиком.
 */
export const getCommentCell = (item: ExpertReportFeedbackItem): FeedbackCellValue => {
  if (item.commentRemark) return { kind: 'diff', previous: '', next: formatFeedbackValue(item.commentValue) };
  return item.commentChanged
    ? {
        kind: 'diff',
        previous: formatFeedbackValue(item.commentPreviousValue),
        next: formatFeedbackValue(item.commentValue ?? item.commentSystemValue),
      }
    : { kind: 'plain', text: formatFeedbackValue(item.commentValue) };
};
